import {
  animate,
  group,
  query,
  stagger,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BedSelection,
  BookingService,
  RateType,
} from '@app/hostel/booking.service';
import { LocaleService } from '@app/locale.service';
import { Currency, Hostel, Room } from '@app/services/api/api.service';
import { AngularSvgIconModule } from 'angular-svg-icon';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subject,
  distinctUntilChanged,
  map,
  takeUntil,
  tap,
} from 'rxjs';

@Component({
  selector: 'app-room',
  standalone: true,
  imports: [CommonModule, AngularSvgIconModule, NgOptimizedImage],
  templateUrl: './room.component.html',
  styleUrls: ['./room.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0.4 }),
        animate('0.15s ease-in', style({ opacity: 1 })),
      ]),
    ]),
    trigger('listFadeSlide', [
      transition(':enter', [
        style({ height: '30px', opacity: 0 }),
        group([
          query(
            '.room',
            [
              style({ opacity: 0 }),
              stagger('100ms', [
                animate('300ms ease-out', style({ opacity: 1 })),
              ]),
            ],
            { optional: true }
          ),
          animate('300ms ease-out', style({ height: '*', opacity: 1 })),
        ]),
      ]),
      transition(':leave', [
        group([
          query(
            '.room',
            [
              stagger('-100ms', [
                animate(
                  '200ms ease-out',
                  style({ transform: 'translateY(0px)', opacity: 0 })
                ),
              ]),
            ],
            { optional: true }
          ),
          animate('300ms ease-out', style({ height: '0px', opacity: 0 })),
        ]),
      ]),
    ]),
  ],
})
export class RoomComponent {
  @Input()
  public hostel: Hostel;

  @Input()
  public room: any;

  private destroyed = new Subject<void>();
  public showList = false;
  public currency: ReplaySubject<Currency>;

  @Output()
  public roomCountChange = new EventEmitter<any>();

  public selectedBeds: Observable<BedSelection>;
  public disableIncrement = new BehaviorSubject(false);
  public disableNonRefundable = new BehaviorSubject(false);
  public disableStandard = new BehaviorSubject(false);

  constructor(
    private readonly localeService: LocaleService,
    private readonly bookingService: BookingService,
    private readonly cdr: ChangeDetectorRef,
    private readonly route: ActivatedRoute
  ) {
    // console.log('room component constructor');
  }

  ngOnInit() {
    this.currency = this.localeService.currentCurrency;

    this.selectedBeds = this.bookingService.selectedBeds$.pipe(
      map((beds) => {
        const roomSelection = beds.get(this.room.id ?? this.room._id);

        if (roomSelection?.rates.standard > 0) {
          this.disableNonRefundable.next(true);
        } else {
          this.disableNonRefundable.next(false);
        }

        if (roomSelection?.rates.nonRefundable > 0) {
          this.disableStandard.next(true);
        } else {
          this.disableStandard.next(false);
        }

        return roomSelection;
      }),
      takeUntil(this.destroyed)
    );

    this.route.queryParams
      .pipe(
        // filter out any events in hwich the only the number of guests changes
        distinctUntilChanged(
          (prev, curr) =>
            prev['checkInDate'] === curr['checkInDate'] &&
            prev['checkOutDate'] === curr['checkOutDate'] &&
            prev['currency'] === curr['currency']
        ),
        tap((params) => {
          // console.log('params', params);
          this.initializeRates();
          for (let rate in this.room.rates) {
            this.room.rates[rate].count = 0;
          }
          this.cdr.detectChanges();
        }),
        takeUntil(this.destroyed)
      )

      .subscribe();
  }

  public increment(rate: RateType) {
    // disable when # of rooms or # of available beds is met
    if (!this.room.rates[rate].disabled) {
      this.room.rates[rate].count += 1;
      // Object.keys(this.room.rates).forEach(r => {
      //   if (rate !== r) {
      //     this.room.rates[r].disabled = true;
      //   }
      // });
      this.updateBookingOptions(rate);
      this.cdr.markForCheck();
    }
    // this.bookingService.addRoom(this.room)
    this.bookingService.addBed(this.room, rate);
    // this.selectedBeds = this.bookingService.getSelectedBeds(this.room);

    if (this.room.roomType === 2) {
      const totalBeds =
        (this.room.rates['standard']?.count ?? 0) +
        (this.room.rates['nonRefundable']?.count ?? 0);

      if (totalBeds >= this.room.noOfAvailableBeds) {
        this.disableIncrement.next(true);
        return;
      }
    } else {
      const totalRooms =
        (this.room.rates['standard']?.count ?? 0) +
        (this.room.rates['nonRefundable']?.count ?? 0);

      if (totalRooms >= this.room.numberOfRooms) {
        this.disableIncrement.next(true);
        return;
      }
    }
  }

  public decrement(rate: RateType) {
    if (this.room.rates[rate] && this.room.rates[rate].count > 0) {
      this.room.rates[rate].count -= 1;
    }
    this.bookingService.removeBed(this.room, rate);
    this.disableIncrement.next(false);
  }

  // move some of this up to the hostel-page
  private updateBookingOptions(rate?: RateType) {
    this.bookingService.bookingOptions.next({
      hostel: this.hostel,
      name: this.room.name,
      description: this.room.description,
      price: this.room.rates[rate]?.price || 0,
      bedCount: this.room.rates[rate]?.count || 0,
      nights: 1,
    });
  }

  public roomHasFreeBreakfast(room: Room): boolean {
    return Boolean(
      room.amenities.find((a: any) => a.names === 'Free breakfast')
    );
  }

  public onImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    const parent = imgElement.parentElement;
    if (parent) {
      const width = imgElement.width;
      const height = imgElement.height;
      parent.innerHTML = `<svg-icon [svgStyle]="{'width.px': ${width}, 'height.px': ${height}}" src="/assets/icons/large_bed.svg"></svg-icon>`;
    }
  }

  // todo reset rate counts on date change?
  private initializeRates() {
    // console.log('initializeRates');
    // TODO min and max won't work on the room
    // each bed needs a price, not a range
    this.room.rates = {
      nonRefundable: {
        price: this.room.nonRefundableRate,
        count: 0,
      },
      standard: {
        price: this.room.standardRate,
        count: 0,
      },
    };

    this.showList = false;
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
